import { useCallback, useEffect, useRef, useState } from "react";
import {
  DISBURSEMENT_FORM_TYPE,
  disbursementDraftId,
  emptyDisbursementData,
  normaliseDisbursementData,
} from "@/lib/disbursements";

// Loads and saves the brand's shared Disbursements record (see src/lib/disbursements.js).
// Works the same way as useBudgetRecoup: loaded once when the screen opens, and
// `change(fn)` takes a function from the current record to the next one so that on a 409
// (a colleague saved first) the same change is re-applied to their newer record.
export function useDisbursements(brandType, draftName) {
  const [data, setData] = useState(emptyDisbursementData);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const versionRef = useRef(null);
  const dataRef = useRef(data);
  dataRef.current = data;

  const readRecord = useCallback(async () => {
    const params = new URLSearchParams({ brand: brandType, form_type: DISBURSEMENT_FORM_TYPE });
    const response = await fetch(`/api/vals-drafts?${params}`, { credentials: "include", cache: "no-store" });
    if (!response.ok) throw new Error(`Disbursements returned ${response.status}`);
    const drafts = await response.json();
    const record = Array.isArray(drafts)
      ? drafts.find((draft) => draft.id === disbursementDraftId(brandType)) || null
      : null;
    return { version: record?.updated_at || null, data: normaliseDisbursementData(record?.form_data) };
  }, [brandType]);

  const reload = useCallback(async () => {
    try {
      const record = await readRecord();
      versionRef.current = record.version;
      setData(record.data);
      setLoadError(false);
    } catch (err) {
      console.warn("Disbursements could not be loaded:", err);
      setLoadError(true);
    } finally {
      setLoaded(true);
    }
  }, [readRecord]);

  useEffect(() => { reload(); }, [reload]);

  const change = useCallback(async (apply) => {
    // Refuse to write over a record we could not read: saving from an empty start would
    // wipe out every disbursement already entered.
    if (loadError) throw new Error("Disbursements did not load, so changes cannot be saved. Reload the page and try again.");
    let next = apply(dataRef.current);
    setData(next);
    setSaving(true);
    try {
      for (let attempt = 0; attempt < 3; attempt += 1) {
        const body = {
          id: disbursementDraftId(brandType),
          form_type: DISBURSEMENT_FORM_TYPE,
          draft_name: draftName,
          brand: brandType,
          form_data: next,
        };
        if (versionRef.current) body.expected_updated_at = versionRef.current;

        const response = await fetch("/api/vals-drafts", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });

        if (response.status === 409) {
          const latest = await readRecord();
          versionRef.current = latest.version;
          next = apply(latest.data);
          setData(next);
          continue;
        }
        if (!response.ok) {
          const payload = await response.json().catch(() => ({}));
          throw new Error(payload?.error || `Disbursements could not be saved (${response.status})`);
        }
        const saved = await response.json().catch(() => null);
        versionRef.current = saved?.updated_at || null;
        return next;
      }
      throw new Error("Someone else kept changing Disbursements at the same time. Reload and try again.");
    } catch (err) {
      // Put the screen back to what is actually stored rather than leave an unsaved change
      // looking saved.
      reload();
      throw err;
    } finally {
      setSaving(false);
    }
  }, [brandType, draftName, loadError, readRecord, reload]);

  return { data, loaded, loadError, saving, change, reload };
}
